import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  TrendingUp, TrendingDown, Coins, RefreshCw, Settings, LogOut, 
  Plus, Edit2, Trash2, ChevronRight, Calendar, Target, Wallet,
  ArrowUpRight, ArrowDownRight, Minus, Info
} from 'lucide-react';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, Area, AreaChart 
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import LanguageSelector from '../components/ui/LanguageSelector';
import AnimatedCounter from '../components/ui/AnimatedCounter';
import ProgressRing from '../components/ui/ProgressRing';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const CURRENCIES = [
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'TRY', symbol: '₺', name: 'Turkish Lira' },
  { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham' }
];

// Dashboard Header Component
const DashboardHeader = ({ user, onLogout, onSettingsClick, t }) => (
  <header className="fixed top-0 left-0 right-0 z-50 glass-dark border-b border-[#1e1e2e]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between h-16">
        <a href="/" className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#D4AF37] to-[#B8960C] flex items-center justify-center">
            <Coins className="w-5 h-5 text-black" />
          </div>
          <span className="font-bold text-lg text-white">
            BuyMe<span className="text-[#D4AF37]">Gold</span>
          </span>
        </a>

        <div className="flex items-center gap-3">
          <LanguageSelector variant="minimal" />
          
          <Button
            onClick={onSettingsClick}
            className="p-2 bg-transparent hover:bg-[#1e1e2e] rounded-lg transition-colors"
          >
            <Settings className="w-5 h-5 text-gray-400" />
          </Button>
          
          <span className="text-sm text-gray-300 hidden sm:block">{user?.name}</span>
          
          <Button
            onClick={onLogout}
            className="p-2 bg-transparent hover:bg-red-500/10 rounded-lg transition-colors"
            title={t('signOut')}
          >
            <LogOut className="w-5 h-5 text-gray-400 hover:text-red-400" />
          </Button>
        </div>
      </div>
    </div>
  </header>
);

// Stats Card Component
const StatsCard = ({ title, value, change, changeType, icon: Icon, currency, isLoading, subtitle }) => {
  const isPositive = changeType === 'positive';
  const isNeutral = changeType === 'neutral';
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-[#12121a] border border-[#1e1e2e] rounded-2xl p-5"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center">
          <Icon className="w-5 h-5 text-[#D4AF37]" />
        </div>
        {change !== undefined && !isLoading && (
          <div className={`flex items-center gap-1 text-sm ${
            isNeutral ? 'text-gray-400' : isPositive ? 'text-emerald-400' : 'text-red-400'
          }`}>
            {isNeutral ? (
              <Minus className="w-4 h-4" />
            ) : isPositive ? (
              <ArrowUpRight className="w-4 h-4" />
            ) : (
              <ArrowDownRight className="w-4 h-4" />
            )}
            <span>{Math.abs(change).toFixed(2)}%</span>
          </div>
        )}
      </div>
      
      <p className="text-sm text-gray-400 mb-1">{title}</p>
      {isLoading ? (
        <div className="h-8 bg-[#1e1e2e] rounded animate-pulse" />
      ) : (
        <>
          <p className="text-2xl font-bold text-white">
            {currency?.symbol}
            <AnimatedCounter value={value || 0} decimals={2} />
          </p>
          {subtitle && (
            <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
          )}
        </>
      )}
    </motion.div>
  );
};

// Performance Chart Component
const PerformanceChart = ({ data, currency, isLoading }) => {
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-[#12121a] border border-[#1e1e2e] rounded-lg p-3 shadow-xl">
          <p className="text-xs text-gray-400 mb-1">{label}</p>
          <p className="text-sm font-semibold text-[#D4AF37]">
            {currency?.symbol}{payload[0].value.toLocaleString()}
          </p>
        </div>
      );
    }
    return null;
  };

  if (isLoading) {
    return (
      <div className="h-[300px] flex items-center justify-center">
        <RefreshCw className="w-6 h-6 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="h-[300px] flex items-center justify-center text-gray-400">
        No historical data available yet
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={300}>
      <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="valueGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#D4AF37" stopOpacity={0.3} />
            <stop offset="95%" stopColor="#D4AF37" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#1e1e2e" vertical={false} />
        <XAxis 
          dataKey="date" 
          tick={{ fontSize: 11, fill: '#6b7280' }}
          tickLine={false}
          axisLine={{ stroke: '#1e1e2e' }}
          tickFormatter={(date) => {
            const d = new Date(date);
            return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          }}
        />
        <YAxis 
          tick={{ fontSize: 11, fill: '#6b7280' }}
          tickLine={false}
          axisLine={false}
          tickFormatter={(value) => `${currency?.symbol}${value}`}
          width={70}
        />
        <Tooltip content={<CustomTooltip />} />
        <Area
          type="monotone"
          dataKey="value"
          stroke="#D4AF37"
          strokeWidth={2}
          fill="url(#valueGradient)"
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

// Settings Modal Component
const SettingsModal = ({ isOpen, onClose, user, onUpdateCurrency }) => {
  const [currency, setCurrency] = useState(user?.preferred_currency || 'EUR');
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    await onUpdateCurrency(currency);
    setIsSaving(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-end sm:items-center justify-center">
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 100 }}
        className="bg-[#12121a] border border-[#1e1e2e] rounded-t-2xl sm:rounded-2xl p-5 sm:p-6 w-full sm:max-w-md sm:m-4"
      >
        <h2 className="text-xl font-bold text-white mb-4">Settings</h2>
        
        <div className="space-y-4">
          <div>
            <Label className="text-gray-300">Preferred Currency</Label>
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger className="h-12 mt-2 bg-[#0a0a0f] border-[#1e1e2e] text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#12121a] border-[#1e1e2e] z-[110]">
                {CURRENCIES.map((c) => (
                  <SelectItem key={c.code} value={c.code} className="text-white hover:bg-[#1e1e2e]">
                    <span className="flex items-center gap-2">
                      <span className="font-medium text-[#D4AF37]">{c.symbol}</span>
                      <span>{c.name}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex gap-3 mt-6 pt-4 border-t border-[#1e1e2e] sm:border-0 sm:pt-0">
          <Button
            onClick={onClose}
            className="flex-1 h-12 sm:h-11 bg-transparent border border-[#1e1e2e] text-gray-300 hover:bg-[#1e1e2e] rounded-xl text-base"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="flex-1 h-12 sm:h-11 btn-gold text-black font-semibold rounded-xl text-base"
          >
            {isSaving ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Save'}
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

// Create/Edit Holding Modal with Date Picker
const HoldingModal = ({ isOpen, onClose, existingHolding, onSave }) => {
  const [grams, setGrams] = useState('');
  const [startDate, setStartDate] = useState('');
  const [historicalPrice, setHistoricalPrice] = useState(null);
  const [isLoadingPrice, setIsLoadingPrice] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Get today's date in YYYY-MM-DD format
  const today = new Date().toISOString().split('T')[0];
  
  // Get 5 years ago (max history available)
  const minDate = new Date();
  minDate.setFullYear(minDate.getFullYear() - 5);
  const minDateStr = minDate.toISOString().split('T')[0];

  useEffect(() => {
    if (isOpen) {
      if (existingHolding) {
        setGrams(existingHolding.grams_owned?.toString() || '');
        // Format existing start date
        const existingDate = existingHolding.start_date 
          ? new Date(existingHolding.start_date).toISOString().split('T')[0]
          : today;
        setStartDate(existingDate);
      } else {
        setGrams('');
        setStartDate(today);
      }
      setHistoricalPrice(null);
    }
  }, [existingHolding, isOpen, today]);

  // Fetch historical price when date changes
  useEffect(() => {
    const fetchPrice = async () => {
      if (!startDate) return;
      
      setIsLoadingPrice(true);
      try {
        const response = await fetch(`${BACKEND_URL}/api/gold/price-on-date?date=${startDate}`);
        if (response.ok) {
          const data = await response.json();
          setHistoricalPrice(data.price_per_gram_eur);
        } else {
          setHistoricalPrice(null);
        }
      } catch (error) {
        console.error('Error fetching historical price:', error);
        setHistoricalPrice(null);
      } finally {
        setIsLoadingPrice(false);
      }
    };

    fetchPrice();
  }, [startDate]);

  const handleSave = async () => {
    if (!grams || parseFloat(grams) <= 0) return;
    
    setIsSaving(true);
    await onSave(parseFloat(grams), startDate);
    setIsSaving(false);
    onClose();
  };

  if (!isOpen) return null;

  const currentValue = historicalPrice && grams 
    ? (parseFloat(grams) * historicalPrice).toFixed(2)
    : null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-end sm:items-center justify-center">
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 100 }}
        className="bg-[#12121a] border border-[#1e1e2e] rounded-t-2xl sm:rounded-2xl p-5 sm:p-6 w-full sm:max-w-md max-h-[90vh] overflow-y-auto sm:m-4"
      >
        <h2 className="text-xl font-bold text-white mb-2">
          {existingHolding ? 'Update Your Holding' : 'Start Tracking Gold'}
        </h2>
        <p className="text-gray-400 text-sm mb-5">
          {existingHolding 
            ? 'Update your gold amount and tracking start date'
            : 'Enter your gold amount and when you started holding it'
          }
        </p>
        
        <div className="space-y-4">
          {/* Gold Amount */}
          <div>
            <Label className="text-gray-300">Gold Amount (grams)</Label>
            <div className="relative mt-2">
              <Coins className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#D4AF37]" />
              <Input
                type="number"
                placeholder="e.g., 100"
                value={grams}
                onChange={(e) => setGrams(e.target.value)}
                className="h-12 pl-11 bg-[#0a0a0f] border-[#1e1e2e] text-white text-lg"
                min="0"
                step="0.01"
              />
            </div>
          </div>

          {/* Start Date */}
          <div>
            <Label className="text-gray-300">Tracking Since</Label>
            <div className="relative mt-2">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#D4AF37]" />
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                max={today}
                min={minDateStr}
                className="h-12 pl-11 bg-[#0a0a0f] border-[#1e1e2e] text-white"
              />
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Choose when you started holding this gold (up to 5 years ago)
            </p>
          </div>

          {/* Historical Price Info */}
          {startDate && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-3 sm:p-4 bg-[#0a0a0f] rounded-xl border border-[#1e1e2e]"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">Gold price on {new Date(startDate).toLocaleDateString()}</span>
                {isLoadingPrice && <RefreshCw className="w-4 h-4 animate-spin text-[#D4AF37]" />}
              </div>
              
              {historicalPrice ? (
                <>
                  <p className="text-xl sm:text-2xl font-bold text-[#D4AF37]">
                    €{historicalPrice.toFixed(2)}<span className="text-sm text-gray-400 ml-1">/gram</span>
                  </p>
                  {currentValue && grams && (
                    <p className="text-sm text-gray-400 mt-2">
                      Initial value: <span className="text-white font-medium">€{currentValue}</span>
                    </p>
                  )}
                </>
              ) : !isLoadingPrice ? (
                <p className="text-gray-500 text-sm">Price data not available for this date</p>
              ) : null}
            </motion.div>
          )}
        </div>

        {/* Buttons - sticky at bottom on mobile */}
        <div className="flex gap-3 mt-5 pt-4 border-t border-[#1e1e2e] sm:border-0 sm:pt-0 sm:mt-6">
          <Button
            onClick={onClose}
            className="flex-1 h-12 sm:h-11 bg-transparent border border-[#1e1e2e] text-gray-300 hover:bg-[#1e1e2e] rounded-xl text-base"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || !grams || parseFloat(grams) <= 0 || !startDate}
            className="flex-1 h-12 sm:h-11 btn-gold text-black font-semibold rounded-xl text-base"
          >
            {isSaving ? <RefreshCw className="w-4 h-4 animate-spin" /> : existingHolding ? 'Update' : 'Start Tracking'}
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

// Empty State Component
const EmptyState = ({ onCreateHolding, t }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-[#12121a] border border-[#1e1e2e] rounded-2xl p-8 text-center"
  >
    <div className="w-16 h-16 rounded-2xl bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-4">
      <Coins className="w-8 h-8 text-[#D4AF37]" />
    </div>
    <h3 className="text-xl font-bold text-white mb-2">{t('startYourGoldJourney')}</h3>
    <p className="text-gray-400 mb-6 max-w-md mx-auto">
      {t('emptyStateDescription')}
    </p>
    <Button
      onClick={onCreateHolding}
      className="btn-gold text-black font-semibold px-8 py-3 rounded-xl"
    >
      <Plus className="w-5 h-5 mr-2" />
      {t('addYourGoldHolding')}
    </Button>
  </motion.div>
);

// Main Dashboard Component
const DashboardPage = () => {
  const { user, logout, updateUser } = useAuth();
  const { t, currentLang } = useLanguage();
  const [holdingsData, setHoldingsData] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [showHoldingModal, setShowHoldingModal] = useState(false);
  const [selectedDays, setSelectedDays] = useState(365); // Default to 1 year to show more history

  const currency = CURRENCIES.find(c => c.code === user?.preferred_currency) || CURRENCIES[0];

  const fetchHoldings = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/holdings`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setHoldingsData(data);
        
        // Auto-select appropriate time range based on days tracked
        if (data?.performance?.days_tracked) {
          const daysTracked = data.performance.days_tracked;
          if (daysTracked <= 7) {
            setSelectedDays(7);
          } else if (daysTracked <= 30) {
            setSelectedDays(30);
          } else if (daysTracked <= 90) {
            setSelectedDays(90);
          } else {
            setSelectedDays(365); // Show full year or all available
          }
        }
      }
    } catch (error) {
      console.error('Error fetching holdings:', error);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/holdings/history?days=${selectedDays}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setHistoryData(data.history || []);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  }, [selectedDays]);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await fetchHoldings();
      setIsLoading(false);
    };
    loadData();
  }, [fetchHoldings]);

  // Fetch history when selectedDays changes or holdings change
  useEffect(() => {
    if (holdingsData?.has_holding) {
      fetchHistory();
    }
  }, [selectedDays, holdingsData?.has_holding, fetchHistory]);

  const handleCreateOrUpdateHolding = async (grams, startDate) => {
    try {
      const payload = { grams_owned: grams };
      if (startDate) {
        payload.start_date = startDate;
      }
      
      const response = await fetch(`${BACKEND_URL}/api/holdings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        await fetchHoldings();
        await fetchHistory();
      }
    } catch (error) {
      console.error('Error saving holding:', error);
    }
  };

  const handleDeleteHolding = async () => {
    if (!window.confirm('Are you sure you want to delete your holding? All tracking history will be lost.')) {
      return;
    }
    
    try {
      await fetch(`${BACKEND_URL}/api/holdings`, {
        method: 'DELETE',
        credentials: 'include'
      });
      setHoldingsData({ has_holding: false });
      setHistoryData([]);
    } catch (error) {
      console.error('Error deleting holding:', error);
    }
  };

  const handleUpdateCurrency = async (newCurrency) => {
    await updateUser({ preferred_currency: newCurrency });
    await fetchHoldings();
    await fetchHistory();
  };

  const performance = holdingsData?.performance;
  const holding = holdingsData?.holding;

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      <DashboardHeader 
        user={user} 
        onLogout={logout}
        onSettingsClick={() => setShowSettings(true)}
        t={t}
      />

      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Welcome Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
            {t('welcomeBack')} {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-gray-400">
            {holdingsData?.has_holding 
              ? t('hereIsPerformance')
              : t('startTracking')
            }
          </p>
        </motion.div>

        {!holdingsData?.has_holding && !isLoading ? (
          <EmptyState onCreateHolding={() => setShowHoldingModal(true)} t={t} />
        ) : (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <StatsCard
                title={t('currentValue')}
                value={performance?.current_value}
                icon={Wallet}
                currency={currency}
                isLoading={isLoading}
              />
              <StatsCard
                title={t('todaysChange')}
                value={Math.abs(performance?.today_change || 0)}
                change={performance?.today_change_percentage}
                changeType={
                  (performance?.today_change_percentage || 0) > 0 ? 'positive' : 
                  (performance?.today_change_percentage || 0) < 0 ? 'negative' : 'neutral'
                }
                icon={performance?.today_change >= 0 ? TrendingUp : TrendingDown}
                currency={currency}
                isLoading={isLoading}
                subtitle={performance?.today_change_per_gram ? 
                  `${performance.today_change_per_gram >= 0 ? '+' : ''}${currency?.symbol}${performance.today_change_per_gram.toFixed(2)}/${t('gram')}` : 
                  null
                }
              />
              <StatsCard
                title={t('totalProfitLoss')}
                value={Math.abs(performance?.profit || 0)}
                change={performance?.profit_percentage}
                changeType={
                  (performance?.profit_percentage || 0) > 0 ? 'positive' : 
                  (performance?.profit_percentage || 0) < 0 ? 'negative' : 'neutral'
                }
                icon={Target}
                currency={currency}
                isLoading={isLoading}
              />
              <StatsCard
                title={t('goldOwned')}
                value={holding?.grams_owned}
                icon={Coins}
                currency={{ symbol: '' }}
                isLoading={isLoading}
                suffix={t('gram')}
              />
            </div>

            {/* Holding Details Card */}
            {holding && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="mb-8"
              >
                <Card className="bg-[#12121a] border-[#1e1e2e] rounded-2xl overflow-hidden">
                  <CardHeader className="border-b border-[#1e1e2e]">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                        <Coins className="w-5 h-5 text-[#D4AF37]" />
                        {t('yourGoldHolding')}
                      </CardTitle>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => setShowHoldingModal(true)}
                          className="h-9 px-3 bg-[#1e1e2e] hover:bg-[#2a2a3a] text-gray-300 rounded-lg"
                        >
                          <Edit2 className="w-4 h-4 mr-1" />
                          {t('edit')}
                        </Button>
                        <Button
                          onClick={handleDeleteHolding}
                          className="h-9 px-3 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {/* Gold Amount with Ring */}
                      <div className="flex items-center gap-4">
                        <ProgressRing
                          progress={Math.min(Math.abs(performance?.profit_percentage || 0), 100)}
                          size={80}
                          strokeWidth={6}
                          color={performance?.profit_percentage >= 0 ? '#10B981' : '#EF4444'}
                        >
                          <Coins className="w-6 h-6 text-[#D4AF37]" />
                        </ProgressRing>
                        <div>
                          <p className="text-sm text-gray-400">{t('goldAmount')}</p>
                          <p className="text-2xl font-bold text-white">{holding.grams_owned}{t('gram')}</p>
                          <p className="text-xs text-gray-500">
                            @ {currency.symbol}{performance?.current_price_per_gram?.toFixed(2)}/{t('gram')}
                          </p>
                        </div>
                      </div>

                      {/* Tracking Info */}
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="w-4 h-4 text-gray-500" />
                          <span className="text-gray-400">{t('trackingSince')}</span>
                          <span className="text-white">
                            {new Date(holding.start_date).toLocaleDateString(currentLang === 'tr' ? 'tr-TR' : currentLang === 'el' ? 'el-GR' : currentLang === 'ar' ? 'ar-SA' : 'en-US')}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Target className="w-4 h-4 text-gray-500" />
                          <span className="text-gray-400">{t('daysTracked')}</span>
                          <span className="text-white">{performance?.days_tracked}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Info className="w-4 h-4 text-gray-500" />
                          <span className="text-gray-400">{t('startPrice')}</span>
                          <span className="text-white">{currency.symbol}{holding.start_price?.toFixed(2)}/{t('gram')}</span>
                        </div>
                      </div>

                      {/* Performance Summary */}
                      <div className="bg-[#0a0a0f] rounded-xl p-4">
                        <p className="text-sm text-gray-400 mb-2">{t('performance')}</p>
                        <div className={`text-3xl font-bold ${
                          performance?.profit_percentage >= 0 ? 'text-emerald-400' : 'text-red-400'
                        }`}>
                          {performance?.profit_percentage >= 0 ? '+' : ''}
                          {performance?.profit_percentage?.toFixed(2)}%
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          {currency.symbol}{performance?.profit >= 0 ? '+' : ''}
                          {performance?.profit?.toFixed(2)} {t('sinceStart')}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Performance Chart */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card className="bg-[#12121a] border-[#1e1e2e] rounded-2xl overflow-hidden">
                <CardHeader className="border-b border-[#1e1e2e]">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <CardTitle className="text-lg font-semibold text-white">
                      Value History
                    </CardTitle>
                    <div className="flex gap-1 bg-[#0a0a0f] rounded-lg p-1 overflow-x-auto">
                      {[
                        { days: 7, label: '7D' },
                        { days: 30, label: '1M' },
                        { days: 90, label: '3M' },
                        { days: 365, label: '1Y' },
                        { days: 1825, label: 'All' },
                      ].map(({ days, label }) => (
                        <Button
                          key={days}
                          onClick={() => setSelectedDays(days)}
                          className={`px-3 py-2 text-sm rounded-lg transition-all whitespace-nowrap ${
                            selectedDays === days
                              ? 'btn-gold text-black'
                              : 'bg-transparent text-gray-400 hover:text-white'
                          }`}
                        >
                          {label}
                        </Button>
                      ))}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <PerformanceChart 
                    data={historyData} 
                    currency={currency}
                    isLoading={isLoading}
                  />
                </CardContent>
              </Card>
            </motion.div>

            {/* Market Info */}
            {holdingsData?.market_data && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="mt-4 text-center"
              >
                <p className="text-xs text-gray-500">
                  Data source: {holdingsData.market_data.source}
                  {holdingsData.market_data.is_fallback && (
                    <span className="ml-2 text-yellow-500">(Indicative)</span>
                  )}
                </p>
              </motion.div>
            )}
          </>
        )}
      </main>

      {/* Modals */}
      <SettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        user={user}
        onUpdateCurrency={handleUpdateCurrency}
      />
      
      <HoldingModal
        isOpen={showHoldingModal}
        onClose={() => setShowHoldingModal(false)}
        existingHolding={holdingsData?.has_holding ? holdingsData.holding : null}
        onSave={handleCreateOrUpdateHolding}
      />
    </div>
  );
};

export default DashboardPage;
